// src/modules/leads-app/components/LeadsApp.tsx
//
// «Leads» como app del sistema operativo Orion (pedido de Samuel, 2026-10-24:
// «el diseño de la app de leads debe ser la del sistema operativo»), hermana de
// la app de WhatsApp: barra superior propia con identidad, KPIs en chips,
// pestañas Lista · Embudo, ficha lateral (en el celular, pantalla con regresar).
//
// 🔒 Datos: SOLO endpoints existentes, org SIEMPRE del JWT —
//   mcp_django /admin/sales/leads (list · count · canales · detalle · PATCH ·
//   reassign · notes · POST alta), Orión /org/users (equipo para reasignar) y
//   /visitor-threads (conversaciones por teléfono), /admin/public-tracking/journey
//   («Ver recorrido»). Vendedora: solo lo suyo (mine=true forzado, sin reasignar).

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Columns3, Download, Filter, KanbanSquare, List, Loader2, RefreshCw, Search, ShieldAlert, Upload, UserPlus, UserX, X,
} from 'lucide-react';

import { useIsMobile } from '@/components/ui/useIsMobile';
import { useModules } from '@/context/ModulesContext';
import {
  countLeads, countLeadsByChannel, getLead, listLeads, patchLead,
  type CreateLeadResult, type LeadDTO, type LeadListParams, type LeadsPorCanalDTO,
} from '@/modules/leads-import';
import { listOrgUsers, type OrgUser } from '@/modules/sales-desk/api';
import {
  civilDay, civilKey, DATE_RANGES, downloadText, isUnanswered, isUnassigned, leadsToCsv, rangeStart, readPref, STAGES,
  stageLabel, writePref, type DateRange, type StageKey,
} from '../model';
import { LeadsBoard, type BoardColumn } from './LeadsBoard';
import { LeadsList } from './LeadsList';
import { LeadSheet } from './LeadSheet';
import { NewLeadDialog } from './NewLeadDialog';
import { LeadsGlyph } from './parts';
import './leads-app.css';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;
type View = 'lista' | 'embudo';
type Quick = '' | 'sin_asignar' | 'sin_respuesta';
type Sort = 'recientes' | 'actualizados' | 'nombre' | 'etapa';

const PAGE = 100;
const COL_PAGE = 40;
const SAMPLE = 500;
const MAX_RANGE_PAGES = 4; // hasta 2,000 leads al filtrar por fecha
const VIEW_KEY = 'orion.leads-app.view';

type Kpis = {
  total: number | null;
  byStage: Partial<Record<StageKey, number | null>>;
  today: number | null;
  week: number | null;
  unassigned: number | null;
  unanswered: number | null;
  sample: LeadDTO[];
  capped: boolean;
};

const emptyCols = (): Record<StageKey, BoardColumn & { pool?: LeadDTO[] }> =>
  Object.fromEntries(STAGES.map((s) => [s.key, { rows: [], total: null, loading: true }])) as any;

/** Leads con filtro de fecha: el backend ordena por fecha de alta (desc), así que se
 *  pagina hasta pasar el corte y el resultado es EXACTO (salvo el tope de 2,000). */
async function loadRange(params: LeadListParams, range: DateRange): Promise<{ rows: LeadDTO[]; truncated: boolean }> {
  const cut = rangeStart(range)!;
  const out: LeadDTO[] = [];
  for (let i = 0; i < MAX_RANGE_PAGES; i++) {
    const batch = await listLeads({ ...params, limit: 500, offset: i * 500 });
    out.push(...batch.filter((l) => civilKey(l.created_at) >= cut));
    const last = batch[batch.length - 1];
    if (batch.length < 500 || (last && civilKey(last.created_at) < cut)) return { rows: out, truncated: false };
  }
  return { rows: out, truncated: true };
}

function sortRows(rows: LeadDTO[], sort: Sort): LeadDTO[] {
  if (sort === 'recientes') return rows;
  const r = [...rows];
  if (sort === 'nombre') r.sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || '', 'es'));
  if (sort === 'actualizados') r.sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
  if (sort === 'etapa') {
    const idx = (s: string) => { const i = STAGES.findIndex((x) => x.key === s); return i < 0 ? 99 : i; };
    r.sort((a, b) => idx(a.status) - idx(b.status));
  }
  return r;
}

export function LeadsApp({ addNotification }: { addNotification?: Notifier }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);
  const { isAdmin, userRoles, isEnabled, availableModules } = useModules();
  const esVendedora = !isAdmin && !!userRoles?.some((r) => ['vendedor', 'socio', 'partner', 'asociado'].includes(r))
    && !userRoles?.some((r) => ['cto', 'developer', 'admin'].includes(r));
  const safeEnabled = (k: string) => { try { return isEnabled(k); } catch { return false; } };
  const waEnabled = safeEnabled('whatsapp_app');
  const convEnabled = safeEnabled('conversaciones');
  const canImport = availableModules.some((m) => m.key === 'leads-import');

  const [toast, setToast] = useState<{ type: string; title: string; message?: string } | null>(null);
  const notify = useCallback((type: 'success' | 'error' | 'info', title: string, message?: string) => {
    if (addNotification) addNotification({ type, title, message, duration: 4000 });
    else { setToast({ type, title, message }); window.setTimeout(() => setToast(null), 4000); }
  }, [addNotification]);

  // ── Vista y filtros ────────────────────────────────────────────
  const [view, setViewState] = useState<View>(() => (readPref(VIEW_KEY) === 'embudo' ? 'embudo' : 'lista'));
  const setView = (v: View) => { setViewState(v); writePref(VIEW_KEY, v); };
  const initialQ = (() => { try { return new URLSearchParams(window.location.search).get('buscar') || ''; } catch { return ''; } })();
  const [q, setQ] = useState(initialQ);
  const [qDeb, setQDeb] = useState(initialQ);
  useEffect(() => { const t = setTimeout(() => setQDeb(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  const [status, setStatus] = useState<string>('');
  const [canal, setCanal] = useState('');
  const [assignee, setAssignee] = useState('');
  const [range, setRange] = useState<DateRange>('');
  const [quick, setQuick] = useState<Quick>('');
  const [mineToggle, setMineToggle] = useState(false);
  const [sort, setSort] = useState<Sort>('recientes');
  const [showFilters, setShowFilters] = useState(false);
  const mine = esVendedora || mineToggle;

  const baseParams = useMemo<LeadListParams>(() => ({
    mine: mine || undefined, search: qDeb || undefined, canal: canal || undefined, assignee: (!esVendedora && assignee) || undefined,
  }), [mine, qDeb, canal, assignee, esVendedora]);

  // ── Equipo (para nombres y reasignar) ──────────────────────────
  const [users, setUsers] = useState<OrgUser[] | null>(null);
  const [usersError, setUsersError] = useState(false);
  useEffect(() => {
    if (esVendedora) return;
    listOrgUsers().then((u) => setUsers(Array.isArray(u) ? u : [])).catch(() => setUsersError(true));
  }, [esVendedora]);
  const names = useMemo(() => Object.fromEntries((users || []).map((u) => [u.user_id, u.full_name || u.email || u.username])), [users]);
  const [canales, setCanales] = useState<LeadsPorCanalDTO | null>(null);

  // ── KPIs ───────────────────────────────────────────────────────
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [denied, setDenied] = useState(false);
  const loadKpis = useCallback(async () => {
    const scope: LeadListParams = mine ? { mine: true } : {};
    try {
      const [total, sample, ...stageTotals] = await Promise.all([
        countLeads(scope),
        listLeads({ ...scope, limit: SAMPLE }),
        ...STAGES.map((s) => countLeads({ ...scope, status: s.key })),
      ]);
      let today: number | null = null;
      let week: number | null = null;
      if (!mine) {
        const [t, w, all] = await Promise.all([
          countLeadsByChannel({ desde: civilDay(0) }), countLeadsByChannel({ desde: civilDay(6) }), countLeadsByChannel(),
        ]);
        today = t?.total ?? null; week = w?.total ?? null;
        setCanales(all);
      }
      const capped = total != null ? total > sample.length : sample.length >= SAMPLE;
      if (today == null && !capped) today = sample.filter((l) => civilKey(l.created_at) === civilDay(0)).length;
      if (week == null && !capped) week = sample.filter((l) => civilKey(l.created_at) >= civilDay(6)).length;
      setKpis({
        total,
        byStage: Object.fromEntries(STAGES.map((s, i) => [s.key, stageTotals[i]])),
        today, week,
        unassigned: sample.filter(isUnassigned).length,
        unanswered: sample.filter(isUnanswered).length,
        sample, capped,
      });
      setDenied(false);
    } catch (e: any) {
      if (e?.status === 403) setDenied(true);
    }
  }, [mine]);
  useEffect(() => { loadKpis(); }, [loadKpis]);
  const kpiTimer = useRef<number | null>(null);
  const refreshKpisSoon = () => {
    if (kpiTimer.current) window.clearTimeout(kpiTimer.current);
    kpiTimer.current = window.setTimeout(() => loadKpis(), 700);
  };

  // ── Lista ──────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [listRows, setListRows] = useState<LeadDTO[]>([]);
  const [listTotal, setListTotal] = useState<number | null>(null);
  const [clientPool, setClientPool] = useState<LeadDTO[] | null>(null); // fecha o filtro rápido
  const [poolNote, setPoolNote] = useState('');
  const [listState, setListState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [listError, setListError] = useState('');
  const reqId = useRef(0);

  const loadList = useCallback(async (p: number) => {
    const id = ++reqId.current;
    setListState('loading');
    const params: LeadListParams = { ...baseParams, status: status || undefined };
    try {
      if (quick) {
        const src = kpis?.sample ?? await listLeads({ ...(mine ? { mine: true } : {}), limit: SAMPLE });
        const rows = src.filter(quick === 'sin_asignar' ? isUnassigned : isUnanswered);
        if (id !== reqId.current) return;
        setClientPool(rows);
        setPoolNote(kpis?.capped ? `Entre los ${SAMPLE} leads más recientes.` : '');
      } else if (range) {
        const { rows, truncated } = await loadRange(params, range);
        if (id !== reqId.current) return;
        setClientPool(rows);
        setPoolNote(truncated ? 'Se muestran los 2,000 más recientes del periodo.' : '');
      } else {
        const [rows, total] = await Promise.all([listLeads({ ...params, limit: PAGE, offset: p * PAGE }), countLeads(params)]);
        if (id !== reqId.current) return;
        setClientPool(null);
        setPoolNote('');
        setListRows(rows);
        setListTotal(total);
      }
      setPage(p);
      setListState('ok');
      setDenied(false);
    } catch (e: any) {
      if (id !== reqId.current) return;
      if (e?.status === 403) setDenied(true);
      setListError(e?.message || 'No se pudieron cargar los leads');
      setListState('error');
    }
  }, [baseParams, status, range, quick, kpis?.sample, kpis?.capped, mine]);

  // ── Embudo ─────────────────────────────────────────────────────
  const [cols, setCols] = useState(emptyCols);
  const loadColumn = useCallback(async (s: StageKey, append = false) => {
    setCols((c) => ({ ...c, [s]: { ...c[s], loading: true, error: null } }));
    const params: LeadListParams = { ...baseParams, status: s };
    try {
      if (range) {
        const { rows } = await loadRange(params, range);
        setCols((c) => ({ ...c, [s]: { rows: rows.slice(0, COL_PAGE), pool: rows, total: rows.length, loading: false } }));
      } else {
        const cur = append ? cols[s].rows : [];
        const [rows, total] = await Promise.all([
          listLeads({ ...params, limit: COL_PAGE, offset: cur.length }),
          append ? Promise.resolve(cols[s].total) : countLeads(params),
        ]);
        setCols((c) => ({ ...c, [s]: { rows: [...cur, ...rows], total: total ?? null, loading: false } }));
      }
    } catch (e: any) {
      if (e?.status === 403) setDenied(true);
      setCols((c) => ({ ...c, [s]: { ...c[s], loading: false, error: e?.message || 'No se pudo cargar' } }));
    }
  }, [baseParams, range, cols]);
  const loadBoard = useCallback(() => { STAGES.forEach((s) => loadColumn(s.key)); }, [loadColumn]);
  const loadMore = (s: StageKey) => {
    const c = cols[s];
    if (c.pool) setCols((x) => ({ ...x, [s]: { ...c, rows: c.pool!.slice(0, c.rows.length + COL_PAGE) } }));
    else loadColumn(s, true);
  };
  const [mobileStage, setMobileStage] = useState<StageKey>('new');

  // Recarga al cambiar vista/filtros.
  const filterKey = JSON.stringify([view, baseParams, status, range, quick]);
  useEffect(() => {
    if (view === 'lista') loadList(0);
    else loadBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // ── Ficha ──────────────────────────────────────────────────────
  const [selected, setSelected] = useState<LeadDTO | null>(null);
  useEffect(() => {
    let id = '';
    try { id = new URLSearchParams(window.location.search).get('lead') || ''; } catch { /* nada */ }
    if (id) getLead(id).then(setSelected).catch(() => notify('error', 'No se encontró ese lead'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Refleja un lead actualizado en lista, embudo y ficha. */
  const upsert = (l: LeadDTO, prevStatus?: string) => {
    const patch = (arr: LeadDTO[]) => arr.map((x) => (x.id === l.id ? { ...x, ...l } : x));
    setListRows(patch);
    setClientPool((p) => (p ? patch(p) : p));
    setSelected((s) => (s && s.id === l.id ? { ...s, ...l } : s));
    setCols((c) => {
      const from = (prevStatus || STAGES.find((s) => c[s.key].rows.some((x) => x.id === l.id))?.key) as StageKey | undefined;
      const next = { ...c };
      if (from && from !== l.status && next[from]) {
        next[from] = { ...next[from], rows: next[from].rows.filter((x) => x.id !== l.id), total: next[from].total != null ? Math.max(0, next[from].total! - 1) : null };
      }
      const to = l.status as StageKey;
      if (next[to]) {
        const has = next[to].rows.some((x) => x.id === l.id);
        next[to] = has ? { ...next[to], rows: patch(next[to].rows) }
          : { ...next[to], rows: [l, ...next[to].rows], total: next[to].total != null ? next[to].total! + (from && from !== to ? 1 : 0) : null };
      }
      return next;
    });
    if (prevStatus !== undefined && prevStatus !== l.status) refreshKpisSoon();
  };

  const moveLead = async (l: LeadDTO, s: StageKey) => {
    if (l.status === s) return;
    const prev = l.status;
    upsert({ ...l, status: s }, prev);
    try {
      const upd = await patchLead(l.id, { status: s });
      upsert(upd, s);
      notify('success', `${l.name || 'Lead'} → ${stageLabel(s)}`);
    } catch (e: any) {
      upsert({ ...l, status: prev }, s);
      notify('error', 'No se movió el lead', e?.status === 403 ? 'Tu rol no puede cambiar etapas.' : e?.message);
    }
  };

  const refreshAll = () => { loadKpis(); if (view === 'lista') loadList(page); else loadBoard(); };

  // ── Alta / importación / exportación ───────────────────────────
  const [newOpen, setNewOpen] = useState(false);
  const onCreated = async (r: CreateLeadResult) => {
    setNewOpen(false);
    notify('success', r.created ? 'Lead registrado' : 'Ya existía: se completó su registro', r.reabierto ? 'Estaba descartado y volvió a Nuevo.' : undefined);
    refreshAll();
    try { setSelected(await getLead(r.id)); } catch { /* la lista ya lo trae */ }
  };
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      let rows: LeadDTO[];
      if (clientPool) rows = clientPool;
      else {
        rows = [];
        const params: LeadListParams = { ...baseParams, status: view === 'lista' ? status || undefined : undefined };
        for (let i = 0; i < 10; i++) {
          const b = await listLeads({ ...params, limit: 500, offset: i * 500 });
          rows.push(...b);
          if (b.length < 500) break;
        }
      }
      downloadText(`leads-${civilDay(0)}.csv`, leadsToCsv(sortRows(rows, sort), names));
      notify('success', `Exportados ${rows.length.toLocaleString('es-MX')} leads`, rows.length >= 5000 ? 'Tope de 5,000 por archivo: acota con filtros.' : undefined);
    } catch (e: any) {
      notify('error', 'No se pudo exportar', e?.message);
    } finally { setExporting(false); }
  };

  // ── Derivados para pintar ──────────────────────────────────────
  const rowsAll = clientPool ? sortRows(clientPool, sort) : sortRows(listRows, sort);
  const rows = clientPool ? rowsAll.slice(page * PAGE, page * PAGE + PAGE) : rowsAll;
  const total = clientPool ? clientPool.length : listTotal;
  const pageCount = total != null ? Math.max(1, Math.ceil(total / PAGE)) : null;
  const from = total === 0 ? 0 : page * PAGE + 1;
  const to = page * PAGE + rows.length;
  const rangeText = total != null ? `${from.toLocaleString('es-MX')}–${to.toLocaleString('es-MX')} de ${total.toLocaleString('es-MX')}` : `${from}–${to}`;
  const listFootnote = [poolNote, !clientPool && sort !== 'recientes' && (listTotal ?? 0) > PAGE ? 'El orden se aplica a esta página.' : ''].filter(Boolean).join(' ');
  const goPage = (p: number) => { if (clientPool) setPage(p); else loadList(p); };

  const activeFilters = [status && view === 'lista', canal, assignee && !esVendedora, range, quick, mineToggle && !esVendedora].filter(Boolean).length;
  const clearFilters = () => { setStatus(''); setCanal(''); setAssignee(''); setRange(''); setQuick(''); setMineToggle(false); setQ(''); };

  const subtitle = kpis?.total != null
    ? `${kpis.total.toLocaleString('es-MX')} ${mine ? (esVendedora ? 'asignados a ti' : 'tuyos') : 'en la organización'}`
    : denied ? 'Sin acceso' : 'Cargando…';

  // ── UI ─────────────────────────────────────────────────────────
  const tabBtn = (k: View, label: string, Icon: typeof List) => (
    <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)}
      className={[
        isMobile ? 'flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[13px]' : 'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px]',
        'font-medium transition-colors',
        view === k ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
      ].join(' ')}>
      <Icon className="h-4 w-4" aria-hidden />{label}
    </button>
  );

  const kpiChip = (key: string, label: string, value: number | null | undefined, opts: { tone?: string; pressed?: boolean; onClick?: () => void; title?: string; icon?: ReactNode; approx?: boolean } = {}) => (
    <button key={key} type="button" onClick={opts.onClick} aria-pressed={opts.onClick ? !!opts.pressed : undefined} title={opts.title} disabled={!opts.onClick}
      className={[
        opts.tone || '',
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-1 text-[12px] transition-colors disabled:cursor-default',
        opts.pressed ? 'border-primary/50 bg-primary/10 text-foreground' : 'border-border bg-card text-muted-foreground hover:enabled:border-primary/40 hover:enabled:text-foreground',
      ].join(' ')}>
      {opts.icon ?? (opts.tone ? <span className="la-dot h-2 w-2 rounded-full" aria-hidden /> : null)}
      <span className="whitespace-nowrap">{label}</span>
      <span className="text-[13.5px] font-semibold text-foreground tabular-nums">
        {value == null ? (kpis ? '—' : '…') : `${value.toLocaleString('es-MX')}${opts.approx ? '+' : ''}`}
      </span>
    </button>
  );

  const kpiStrip = (
    <div role="group" aria-label="Indicadores de leads"
      className={`flex gap-1.5 px-3 pb-2.5 pt-0.5 sm:px-4 ${isMobile ? 'no-scrollbar overflow-x-auto' : 'flex-wrap'}`}>
      {kpiChip('hoy', 'Nuevos hoy', kpis?.today, { pressed: range === 'hoy', onClick: () => { setQuick(''); setRange(range === 'hoy' ? '' : 'hoy'); }, icon: <span className="la-glyph h-2 w-2 rounded-full" aria-hidden /> })}
      {kpiChip('sem', '7 días', kpis?.week, { pressed: range === '7d', onClick: () => { setQuick(''); setRange(range === '7d' ? '' : '7d'); } })}
      <span className="hidden w-px self-stretch bg-border xl:block" aria-hidden />
      {STAGES.map((s) => kpiChip(s.key, s.label, kpis?.byStage[s.key], {
        tone: s.tone, title: s.hint, pressed: view === 'lista' && status === s.key,
        onClick: () => {
          if (view === 'embudo') { setMobileStage(s.key); document.querySelector(`[aria-label="Etapa ${s.label}"]`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' }); }
          else { setQuick(''); setStatus(status === s.key ? '' : s.key); }
        },
      }))}
      <span className="hidden w-px self-stretch bg-border xl:block" aria-hidden />
      {!esVendedora && kpiChip('sa', 'Sin asignar', kpis?.unassigned, {
        icon: <UserX className="h-3.5 w-3.5" aria-hidden />, pressed: quick === 'sin_asignar', approx: kpis?.capped,
        title: kpis?.capped ? `Entre los ${SAMPLE} más recientes` : 'Sin vendedora asignada',
        onClick: () => { setView('lista'); setRange(''); setQuick(quick === 'sin_asignar' ? '' : 'sin_asignar'); },
      })}
      {kpiChip('sr', 'Sin respuesta', kpis?.unanswered, {
        icon: <span className="h-2 w-2 rounded-full bg-destructive" aria-hidden />, pressed: quick === 'sin_respuesta', approx: kpis?.capped,
        title: 'Siguen en «Nuevo» después de 24 h' + (kpis?.capped ? ` (entre los ${SAMPLE} más recientes)` : ''),
        onClick: () => { setView('lista'); setRange(''); setQuick(quick === 'sin_respuesta' ? '' : 'sin_respuesta'); },
      })}
    </div>
  );

  const selectCls = 'h-9 min-w-0 max-w-[176px] rounded-lg border border-input bg-background px-2 text-[13px] text-foreground';
  const filterControls = (
    <>
      {view === 'lista' && (
        <select aria-label="Etapa" value={status} onChange={(e) => { setQuick(''); setStatus(e.target.value); }} className={selectCls}>
          <option value="">Todas las etapas</option>
          {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      )}
      <select aria-label="Fuente" value={canal} onChange={(e) => setCanal(e.target.value)} className={selectCls}>
        <option value="">Todas las fuentes</option>
        {(canales?.por_canal || []).map((c) => <option key={c.canal} value={c.canal}>{c.etiqueta} ({c.total.toLocaleString('es-MX')})</option>)}
        {!canales && canal && <option value={canal}>{canal}</option>}
      </select>
      {!esVendedora && (
        <select aria-label="Vendedora" value={assignee} onChange={(e) => setAssignee(e.target.value)} className={selectCls} disabled={!users?.length}>
          <option value="">{users?.length ? 'Todas las vendedoras' : 'Vendedora'}</option>
          {(users || []).filter((u) => u.is_seller).map((u) => <option key={u.user_id} value={u.user_id}>{u.full_name || u.email || u.username}</option>)}
          {(users || []).some((u) => !u.is_seller) && (
            <optgroup label="Equipo">
              {(users || []).filter((u) => !u.is_seller).map((u) => <option key={u.user_id} value={u.user_id}>{u.full_name || u.email || u.username}</option>)}
            </optgroup>
          )}
        </select>
      )}
      <select aria-label="Fecha de llegada" value={range} onChange={(e) => { setQuick(''); setRange(e.target.value as DateRange); }} className={selectCls}>
        {DATE_RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
      </select>
      {view === 'lista' && (
        <select aria-label="Ordenar" value={sort} onChange={(e) => setSort(e.target.value as Sort)} className={selectCls}>
          <option value="recientes">Más recientes</option>
          <option value="actualizados">Última actividad</option>
          <option value="nombre">Nombre A–Z</option>
          <option value="etapa">Por etapa</option>
        </select>
      )}
      {!esVendedora && (
        <button type="button" aria-pressed={mineToggle} onClick={() => setMineToggle((v) => !v)}
          className={`h-9 rounded-lg border px-3 text-[13px] font-medium ${mineToggle ? 'border-primary/50 bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}>
          Solo míos
        </button>
      )}
    </>
  );

  const toolbar = (
    <div className="border-y border-border bg-card px-3 py-2 sm:px-4">
      <div className="flex items-center gap-2">
        <label className="relative min-w-0 flex-1 sm:w-56 sm:flex-none">
          <span className="sr-only">Buscar leads</span>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, correo o teléfono"
            className="h-9 w-full rounded-lg border border-input bg-background pl-8 pr-3 text-[13.5px] text-foreground" />
        </label>
        {isMobile ? (
          <button type="button" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters} aria-controls="la-filters"
            className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[13px] font-medium ${activeFilters ? 'border-primary/50 bg-primary/10 text-foreground' : 'border-border text-foreground'}`}>
            <Filter className="h-4 w-4" aria-hidden />Filtros{activeFilters ? ` · ${activeFilters}` : ''}
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{filterControls}</div>
        )}
        {(activeFilters > 0 || q) && !isMobile && (
          <button type="button" onClick={clearFilters} className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg px-2.5 text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground">
            <X className="h-4 w-4" aria-hidden />Limpiar
          </button>
        )}
        <button type="button" onClick={refreshAll} aria-label="Actualizar"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border text-muted-foreground hover:bg-muted hover:text-foreground">
          <RefreshCw className={`h-4 w-4 ${listState === 'loading' && view === 'lista' ? 'animate-spin' : ''}`} aria-hidden />
        </button>
      </div>
      {isMobile && showFilters && (
        <div id="la-filters" className="la-in mt-2 grid grid-cols-2 gap-2 [&>*]:w-full">
          {filterControls}
          {(activeFilters > 0 || q) && (
            <button type="button" onClick={clearFilters} className="h-9 rounded-lg text-[13px] text-muted-foreground hover:bg-muted">Limpiar filtros</button>
          )}
        </div>
      )}
    </div>
  );

  const emptyList = (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-2xl border border-dashed border-border px-6 py-10 text-center">
      <LeadsGlyph size={44} />
      {activeFilters || qDeb ? (
        <>
          <p className="text-[15px] font-semibold text-foreground">Nada coincide con estos filtros</p>
          <p className="text-[13px] text-muted-foreground">Prueba con otra etapa, fuente o fecha, o busca por teléfono sin lada.</p>
          <button type="button" onClick={clearFilters} className="rounded-xl border border-border px-3.5 py-2 text-[13px] font-medium text-foreground hover:bg-muted">Limpiar filtros</button>
        </>
      ) : esVendedora ? (
        <>
          <p className="text-[15px] font-semibold text-foreground">Aún no tienes leads asignados</p>
          <p className="text-[13px] text-muted-foreground">Cuando administración te asigne uno aparecerá aquí, con su ficha y sus conversaciones.</p>
        </>
      ) : (
        <>
          <p className="text-[15px] font-semibold text-foreground">Todavía no hay leads</p>
          <p className="text-[13px] text-muted-foreground">Llegan solos desde tu sitio, WhatsApp, Messenger y anuncios. También puedes registrar uno o importar tu cartera.</p>
          <div className="flex flex-wrap justify-center gap-2">
            <button type="button" onClick={() => setNewOpen(true)} className="la-btn-brand inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-[13px] font-semibold"><UserPlus className="h-4 w-4" aria-hidden />Nuevo lead</button>
            {canImport && <button type="button" onClick={() => navigate('/m/leads-import/upload')} className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3.5 py-2 text-[13px] font-medium text-foreground hover:bg-muted"><Upload className="h-4 w-4" aria-hidden />Importar</button>}
          </div>
        </>
      )}
    </div>
  );

  const deniedState = (
    <div className="grid h-full place-items-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-2 text-center">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-muted text-muted-foreground"><ShieldAlert className="h-5 w-5" aria-hidden /></span>
        <p className="text-[15px] font-semibold text-foreground">Tu rol no tiene acceso a los leads</p>
        <p className="text-[13px] text-muted-foreground">Pide a administración el permiso de ventas para ver y trabajar los leads de la organización.</p>
      </div>
    </div>
  );

  const body = denied ? deniedState : view === 'lista' ? (
    listState === 'error' && rows.length === 0 ? (
      <div className="p-4"><p role="alert" className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{listError}</p></div>
    ) : listState === 'loading' && rows.length === 0 ? (
      <p className="flex items-center gap-2 p-6 text-[13px] text-muted-foreground" role="status"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />Cargando leads…</p>
    ) : (
      <LeadsList rows={rows} names={names} activeId={selected?.id || null} onOpen={setSelected} onMove={moveLead}
        isMobile={isMobile} compact={!!selected} page={page} pageCount={pageCount} onPage={goPage} rangeText={rangeText}
        footnote={listFootnote} empty={emptyList} loading={listState === 'loading'} />
    )
  ) : (
    <LeadsBoard columns={cols} names={names} activeId={selected?.id || null} onOpen={setSelected} onMove={moveLead} onLoadMore={loadMore}
      isMobile={isMobile} mobileStage={mobileStage} onMobileStage={setMobileStage}
      footnote="Arrastra una tarjeta a otra etapa o usa «Mover a…». Cada cambio queda en la actividad del lead." />
  );

  const sheet = selected && (
    <LeadSheet lead={selected} names={names} users={users} usersError={usersError} canReassign={!esVendedora}
      waEnabled={waEnabled} convEnabled={convEnabled} isMobile={isMobile}
      onClose={() => setSelected(null)} onChanged={upsert} notify={notify} />
  );

  const actionBtn = 'inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50';
  const portalTarget = typeof document !== 'undefined' ? document.querySelector('.orion-shell') : null;

  return (
    <div className="leads-app flex w-full flex-col text-foreground" style={{ height: isMobile ? 'calc(100dvh - 72px)' : 'calc(100dvh - 150px)', minHeight: 520 }}>
      <div className="orion-card flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        {/* Barra superior de la app */}
        <header className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:flex-none">
            <LeadsGlyph />
            <div className="min-w-0">
              <h1 className="text-[16px] font-semibold leading-tight">Leads</h1>
              <p className="truncate text-[12px] text-muted-foreground tabular-nums">{subtitle}</p>
            </div>
          </div>
          <nav role="tablist" aria-label="Vistas de Leads" className={isMobile ? 'order-3 grid w-full grid-cols-2 gap-1 rounded-xl bg-muted/50 p-0.5' : 'flex items-center gap-1'}>
            {tabBtn('lista', 'Lista', List)}
            {tabBtn('embudo', 'Embudo', isMobile ? Columns3 : KanbanSquare)}
          </nav>
          <div className="flex items-center gap-2 sm:ml-auto">
            <button type="button" onClick={exportCsv} disabled={exporting || denied} className={actionBtn} aria-label="Exportar a CSV" title="Exportar lo filtrado a CSV (Excel)">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
              <span className="hidden lg:inline">Exportar</span>
            </button>
            {canImport && (
              <button type="button" onClick={() => navigate('/m/leads-import/upload')} className={actionBtn} aria-label="Importar leads" title="Importar cartera (CSV exportado de Excel)">
                <Upload className="h-4 w-4" aria-hidden /><span className="hidden lg:inline">Importar</span>
              </button>
            )}
            <button type="button" onClick={() => setNewOpen(true)} disabled={denied}
              className="la-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-[13px] font-semibold" aria-label="Nuevo lead">
              <UserPlus className="h-4 w-4" aria-hidden /><span className={isMobile ? 'sr-only' : ''}>Nuevo lead</span>
            </button>
          </div>
        </header>

        {isMobile && selected ? (
          <div className="min-h-0 flex-1 border-t border-border">{sheet}</div>
        ) : (
          <>
            {!denied && kpiStrip}
            {!denied && toolbar}
            <div className="flex min-h-0 flex-1 bg-background/40">
              <div className="min-h-0 min-w-0 flex-1">{body}</div>
              {!isMobile && sheet}
            </div>
          </>
        )}
      </div>

      {newOpen && <NewLeadDialog onClose={() => setNewOpen(false)} onCreated={onCreated} portalTarget={portalTarget} />}

      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4">
        {toast && (
          <div role="status" className={`pointer-events-auto max-w-md rounded-xl border px-4 py-2.5 text-[13.5px] shadow-lg ${toast.type === 'error' ? 'border-destructive/50 bg-card text-destructive' : 'border-border bg-card text-foreground'}`}>
            <strong className="font-semibold">{toast.title}</strong>{toast.message ? <span className="text-muted-foreground"> · {toast.message}</span> : null}
          </div>
        )}
      </div>
    </div>
  );
}
